/**
 * VariantSelectorElement - Simple variant selector with chip UI
 * Handles product variant selection without subscription/selling plan logic
 */
class VariantSelectorElement extends HTMLElement {
  constructor() {
    super();

    // State management
    this.isActive = false;
    this.mutationObserver = null;

    // Cached DOM elements
    this.elements = {};

    // Bind methods
    this.handleVariantOptionChange = this.handleVariantOptionChange.bind(this);
    this.handleExternalVariantChange = this.handleExternalVariantChange.bind(this);
    this.handleActiveStateChange = this.handleActiveStateChange.bind(this);
  }

  /**
   * Called when element is added to DOM
   */
  connectedCallback() {
    // Expose methods for external access
    this.forceActivate = () => {
      this.classList.add('active');
      this.selectFirstVariant();
    };

    this.forceUpdate = () => {
      this.detectActiveState();
      if (this.isActive) {
        this.updateSelectedVariant();
      }
    };

    // Initialize after render
    setTimeout(() => {
      this.initializeComponent();
    }, 50);
  }

  /**
   * Called when element is removed from DOM
   */
  disconnectedCallback() {
    this.removeEventListeners();
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }
  }

  /**
   * Initialize component
   */
  initializeComponent() {
    this.cacheElements();
    this.detectActiveState();
    this.attachEventListeners();
    this.initializeSelection();
    this.setupMutationObserver();
  }

  /**
   * Cache DOM elements
   */
  cacheElements() {
    // Variant option inputs within this element
    this.elements.variantOptions = this.querySelectorAll('.variant-option__input');

    // Find closest form context
    const form = this.closest('form') || this.parentElement?.closest('form') || this.parentElement?.querySelector('form');
    const queryContext = form || this.parentElement || document;

    // Form inputs (shared across selector instances)
    this.elements.variantInput = queryContext.querySelector('input[name="id"]');
    this.elements.quantityInput = queryContext.querySelector('input[name="quantity"]');
    
    // Clear any selling plan or property inputs since we're just doing simple variants
    this.elements.sellingPlanInput = queryContext.querySelector('input[name="selling_plan"]');
    
    // Specific caching for the .months_input class as requested
    this.elements.monthsInputClass = queryContext.querySelector('.months_input'); 
    this.elements.monthsInputClass.remove();

    // Price display elements
    this.elements.totalPriceElement = queryContext.querySelector('.purchase-summary__total-price');
    this.elements.originalPriceElement = queryContext.querySelector('.purchase-summary__original-price');
    this.elements.originalAmountElement = queryContext.querySelector('.purchase-summary__original-amount');
    this.elements.savingsElement = queryContext.querySelector('.purchase-summary__savings');
    this.elements.savingsAmountElement = queryContext.querySelector('.purchase-summary__savings-amount');
    this.elements.buttonPrice = queryContext.querySelector('.button-price');
    this.elements.buttonComparePrice = queryContext.querySelector('.button-compare-price');

    console.log('Cached elements for variant selector:', this.id, {
      hasForm: !!form,
      hasVariantInput: !!this.elements.variantInput,
      hasMonthsInput: !!this.elements.monthsInputClass, // Log existence
      optionCount: this.elements.variantOptions.length
    });
  }

  /**
   * Detect if this selector is active
   */
  detectActiveState() {
    try {
      const hasActiveClass = this.classList.contains('active');
      const isOnlySelector = document.querySelectorAll('variant-selector').length === 1;
      
      // Check if parent container is active
      const parentContainer = this.closest('[id^="variant-selector-"]');
      const parentIsActive = parentContainer ? parentContainer.classList.contains('active') : false;

      // Check if this is default when no explicit active state
      let isDefaultActive = false;
      if (!hasActiveClass && !parentIsActive && !isOnlySelector) {
        const allSelectors = document.querySelectorAll('variant-selector');
        if (allSelectors.length > 0 && allSelectors[0] === this) {
          const anyParentActive = Array.from(allSelectors).some(selector => {
            const container = selector.closest('[id^="variant-selector-"]');
            return container && container.classList.contains('active');
          });
          if (!anyParentActive) {
            isDefaultActive = true;
          }
        }
      }

      const previousState = this.isActive;
      this.isActive = hasActiveClass || parentIsActive || isOnlySelector || isDefaultActive;

      console.log('Variant selector active state:', {
        element: this.id,
        hasActiveClass,
        parentIsActive,
        isOnlySelector,
        isDefaultActive,
        finalState: this.isActive,
        stateChanged: previousState !== this.isActive
      });

      return this.isActive;
    } catch (error) {
      console.error('Error detecting active state:', error);
      this.isActive = false;
      return false;
    }
  }

  /**
   * Attach event listeners
   */
  attachEventListeners() {
    // Use event delegation for variant option changes
    this.addEventListener('change', (e) => {
      if (e.target.matches('.variant-option__input')) {
        this.handleVariantOptionChange(e);
      }
    });

    // Listen for external variant changes
    window.addEventListener('variant:change', this.handleExternalVariantChange);
    document.addEventListener('variant:change', this.handleExternalVariantChange);
  }

  /**
   * Remove event listeners
   */
  removeEventListeners() {
    window.removeEventListener('variant:change', this.handleExternalVariantChange);
    document.removeEventListener('variant:change', this.handleExternalVariantChange);
  }

  /**
   * Handle variant option selection
   */
  handleVariantOptionChange(e) {
    const input = e.target;

    // Update UI: remove selected class from all options
    this.querySelectorAll('.variant-option').forEach(option => {
      option.classList.remove('variant-option--selected');
    });

    // Add selected class to current option
    const parentLabel = input.closest('.variant-option');
    if (parentLabel) {
      parentLabel.classList.add('variant-option--selected');
    }

    // Update form inputs and pricing if active
    if (this.isActive) {
      this.updateSelectedVariant();
    }
  }

  /**
   * Update the selected variant in form and pricing
   */
  updateSelectedVariant() {
    const selectedOption = this.querySelector('.variant-option__input:checked');

    if (!selectedOption) {
      console.log('No variant option selected');
      return;
    }

    console.log('Updating selected variant for:', this.id, 'isActive:', this.isActive);

    // Only update form inputs if this element is active
    if (this.isActive && this.elements.variantInput) {
      // Set variant ID
      this.elements.variantInput.value = selectedOption.dataset.variantId || selectedOption.value;

      // Clear selling plan (we're not using subscriptions)
      if (this.elements.sellingPlanInput) {
        this.elements.sellingPlanInput.value = '';
      }

      // Set quantity to 1 (simple variant selection)
      if (this.elements.quantityInput) {
        this.elements.quantityInput.value = '1';
      }

      // Clear any property inputs
      if (this.elements.propertyInput) {
        this.elements.propertyInput.value = '';
        // Also disable standard property input if it exists
        this.elements.propertyInput.disabled = true; 
      }

      console.log('Updated form inputs:', {
        variantId: this.elements.variantInput.value,
        quantity: this.elements.quantityInput ? this.elements.quantityInput.value : 'N/A'
      });
    }

    // Update pricing display
    if (this.isActive) {
      const price = selectedOption.dataset.price;
      const comparePrice = selectedOption.dataset.comparePrice;
      this.updatePricingDisplay(price, comparePrice);
    }

    // Trigger custom event
    window.dispatchEvent(
      new CustomEvent('variant-selector:change', {
        detail: {
          variantId: selectedOption.dataset.variantId || selectedOption.value,
          price: selectedOption.dataset.price,
          comparePrice: selectedOption.dataset.comparePrice
        }
      })
    );
  }

  /**
   * Update pricing display
   */
  updatePricingDisplay(price, comparePrice) {
    // Update main price
    if (this.elements.totalPriceElement && price) {
      this.elements.totalPriceElement.textContent = this.formatMoney(price);
    }

    if (this.elements.buttonPrice && price) {
      this.elements.buttonPrice.textContent = this.formatMoney(price);
    }

    // Handle compare price and savings
    if (comparePrice && parseInt(comparePrice) > parseInt(price)) {
      const savings = parseInt(comparePrice) - parseInt(price);

      // Show original price
      if (this.elements.originalPriceElement && this.elements.originalAmountElement) {
        this.elements.originalAmountElement.textContent = this.formatMoney(comparePrice);
        this.elements.originalPriceElement.style.display = 'block';
      }

      // Show savings
      if (this.elements.savingsElement && this.elements.savingsAmountElement) {
        this.elements.savingsAmountElement.textContent = this.formatMoney(savings);
        this.elements.savingsElement.style.display = 'block';
      }

      // Show button compare price
      if (this.elements.buttonComparePrice) {
        this.elements.buttonComparePrice.textContent = this.formatMoney(comparePrice);
        this.elements.buttonComparePrice.style.display = 'inline';
      }
    } else {
      // Hide compare price and savings
      if (this.elements.originalPriceElement) {
        this.elements.originalPriceElement.style.display = 'none';
      }
      if (this.elements.savingsElement) {
        this.elements.savingsElement.style.display = 'none';
      }
      if (this.elements.buttonComparePrice) {
        this.elements.buttonComparePrice.style.display = 'none';
      }
    }
  }

  /**
   * Format money with fallback
   */
  formatMoney(cents) {
    // Use Shopify.formatMoney if available
    if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(cents);
    }

    // Fallback formatting
    if (typeof cents === 'string') {
      cents = cents.replace('.', '');
    }

    const dollars = (cents / 100).toFixed(2);
    return `$${dollars}`;
  }

  /**
   * Handle external variant changes
   */
  handleExternalVariantChange(e) {
    // Only respond if we're active
    if (!this.isActive) return;

    console.log('External variant change detected for:', this.id);
    
    // Re-detect active state
    this.detectActiveState();

    // Update our display if still active
    if (this.isActive) {
      setTimeout(() => {
        this.updateSelectedVariant();
      }, 50);
    }
  }

  /**
   * Initialize selection on load
   */
  initializeSelection() {
    console.log('Initializing variant selection for:', this.id, 'isActive:', this.isActive);

    // Select first variant option
    this.selectFirstVariant();

    // Re-check active state after delay
    setTimeout(() => {
      const wasActive = this.isActive;
      this.detectActiveState();

      if (!wasActive && this.isActive) {
        console.log('Variant selector became active after delayed detection:', this.id);
        this.updateSelectedVariant();
      } else if (this.isActive) {
        console.log('Variant selector was already active, updating:', this.id);
        this.updateSelectedVariant();
      }
    }, 200);
  }

  /**
   * Select the first variant
   */
  selectFirstVariant() {
    const firstOption = this.querySelector('.variant-option__input');
    
    if (firstOption) {
      // Clear all selections
      this.querySelectorAll('.variant-option').forEach(option => {
        option.classList.remove('variant-option--selected');
      });

      // Select first option
      firstOption.checked = true;
      const parentLabel = firstOption.closest('.variant-option');
      if (parentLabel) {
        parentLabel.classList.add('variant-option--selected');
      }

      // Trigger change event
      const changeEvent = new Event('change', { bubbles: true });
      firstOption.dispatchEvent(changeEvent);

      console.log('Selected first variant:', firstOption.dataset.variantId);
    }
  }

  /**
   * Setup mutation observer for active class changes
   */
  setupMutationObserver() {
    this.mutationObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          this.handleActiveStateChange();
        }
      });
    });

    this.mutationObserver.observe(this, {
      attributes: true,
      attributeFilter: ['class']
    });
  }

  /**
   * Handle active state changes
   */
  handleActiveStateChange() {
    const wasActive = this.isActive;
    this.detectActiveState();

    // If just became active, update
    if (!wasActive && this.isActive) {
      console.log('Variant selector became active:', this.id);
      setTimeout(() => {
        this.updateSelectedVariant();
      }, 50);
    }
  }
}

// Register custom element
customElements.define('variant-selector', VariantSelectorElement);